import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../utils/errorToast.tsx'
import {
  hasEmailValidationError,
  type TeamMember,
  type TeamRelationshipPayload,
  type RelationshipCategoryOption,
  type RelationshipCategorySelectOption,
  parseContactNameFromLabel,
  detectMemberChanges,
  determineContactAction,
  buildRelationshipObject,
  transformAndValidateRelationships,
  handleFounderStatusChange,
  getComparableContact,
  isDuplicateTeamMember,
  categoryIdOf
} from './utils.ts'
import { getAuthHeaders } from '../../../lib/apiRequest.js'
import {
  TOAST_CONFIG,
  TOAST_SUCCESS_CONFIG,
  TOAST_INFO_CONFIG,
  generateNewKey,
  isNewEntry
} from '../../FormTable/constants.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../utils/endpoints.ts'
import {
  createCustomValidationRule,
  createValidationRule,
  mergeValidationSets,
  validateAndShowErrors,
  VALIDATION_TYPES
} from '../../FormTable/validation.ts'
import {
  validateCompanyTeamEntry,
  type CompanyTeamEntry,
  CompanyTeamEntryFields
} from '../../../../state_manager/data_models/company_team_entry.ts'
import {
  ACTION_TYPE_INSERT,
  ACTION_TYPE_UPDATE,
  ACTION_TYPE_DELETE,
  ACTION_TYPE_RESIGN,
  RELATIONSHIP_TYPE_EMPLOYEE,
  RELATIONSHIP_TYPE_FOUNDER
} from '../../../../state_manager/globals.ts'
import type { RootState } from '../../../../state_manager/store.ts'

const processCompanyTeamRelationships = async (companyId: string, teamMembersData: any[]): Promise<any> => {
  try {
    const BASE_URL = getBackendBaseUrl()
    const headers = await getAuthHeaders()

    const response = await fetch(buildFullUrl(BASE_URL, API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM), {
      method: API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM.method,
      headers: {
        ...headers,
        'account-id': companyId
      },
      body: JSON.stringify(teamMembersData)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error calling contact relationships API:', error)
    throw error
  }
}

const handleAPIError = (result: any, defaultMessage: string): never => {
  showErrorToast(defaultMessage, TOAST_CONFIG)
  throw new Error(result.error || defaultMessage)
}

const transformExistingMemberForAPI = (
  currentMember: TeamMember,
  originalMember: TeamMember,
  companyId: string,
  founderCategoryOption: RelationshipCategoryOption | null,
  isExEmployees: boolean = false
): any[] => {
  // Payloads plus the delete stub handleFounderStatusChange pushes when founder status is removed.
  const relationships: (TeamRelationshipPayload | CompanyTeamEntry)[] = []

  const relationshipId = currentMember.key

  // saveRow validates the Name field before transforming, so a member without a contact never reaches here.
  const contact = currentMember.contact
  if (!contact) {
    throw new Error('Team member has no contact')
  }

  const contactId = contact.value === 'new' ? crypto.randomUUID() : contact.value
  const categoryId = categoryIdOf(currentMember.category)

  const changes = detectMemberChanges(currentMember, originalMember)

  const { firstName, lastName } = parseContactNameFromLabel(contact)

  const contactAction = determineContactAction(contact.value, changes.emailChanged, changes.mobileChanged)

  handleFounderStatusChange(
    changes,
    currentMember,
    originalMember,
    relationships,
    companyId,
    contactId,
    categoryId,
    founderCategoryOption,
    firstName,
    lastName,
    contactAction,
    isExEmployees
  )

  if (
    changes.contactChanged ||
    changes.categoryChanged ||
    changes.jobTitleChanged ||
    changes.emailChanged ||
    changes.mobileChanged
  ) {
    const employeeRelationshipId =
      currentMember.employeeRelationshipId ||
      originalMember?.employeeRelationshipId ||
      (currentMember.founder ? null : relationshipId)
    const founderRelationshipId = currentMember.founderRelationshipId || originalMember?.founderRelationshipId || null

    if (employeeRelationshipId) {
      relationships.push(
        buildRelationshipObject({
          relationshipId: employeeRelationshipId,
          relationshipType: RELATIONSHIP_TYPE_EMPLOYEE,
          categoryId,
          action: ACTION_TYPE_UPDATE,
          companyId,
          contactId,
          member: currentMember,
          firstName,
          lastName,
          contactAction,
          isFounder: false,
          isExEmployees
        })
      )
    }

    const hasFounderRelationship = founderRelationshipId !== null
    const shouldUpdateFounder = currentMember.founder || (hasFounderRelationship && originalMember?.founder)

    if (shouldUpdateFounder) {
      const founderCategoryId = founderCategoryOption?.id || categoryId

      const founderAction = founderRelationshipId ? ACTION_TYPE_UPDATE : ACTION_TYPE_INSERT

      relationships.push(
        buildRelationshipObject({
          relationshipId: founderRelationshipId,
          relationshipType: RELATIONSHIP_TYPE_FOUNDER,
          categoryId: founderCategoryId,
          action: founderAction,
          companyId,
          contactId,
          member: currentMember,
          firstName,
          lastName,
          contactAction,
          isFounder: true,
          isExEmployees
        })
      )
    }
  }

  if (relationships.length === 0) {
    return []
  }

  return transformAndValidateRelationships(relationships)
}

interface UseCompanyTeamControllerProps {
  mode?: 'new' | 'existing'
  companyId?: string | null
  initialTeamMembers?: TeamMember[]
  onChange?: ((members: TeamMember[], getTeamMembersForAPI?: (recordId: string) => any[]) => void) | null
  recordId?: string | null
  enforcedFounderStatus?: boolean | null
  hideCategoryColumn?: boolean
  isExEmployees?: boolean
}

interface FirstErrorFieldKey {
  rowKey: string | number
  fieldKey: string
}

interface EmailWarning {
  contact: {
    id: string
    name: string
    email?: string
    mobile_phone?: string
  }
  email: string
}

interface UseCompanyTeamControllerReturn {
  teamMembers: TeamMember[]
  editingRowKey: string | number | null
  availableCategoryOptions: RelationshipCategorySelectOption[]
  founderCategoryOption: RelationshipCategoryOption | null
  isAddingNewMember: boolean
  savingRowKey: string | number | null
  deletingRowKey: string | number | null
  validationErrors: Record<string, Record<string, string>>
  emailWarnings: Record<string | number, EmailWarning>
  firstErrorFieldKey: FirstErrorFieldKey | null
  fieldResetKeys: Record<string | number, number>
  savedEntries: Set<string | number>
  handleAddMember: () => void
  handleRemoveMember: (key: string | number) => Promise<void>
  handleResignMember: (key: string | number) => Promise<void>
  handleFieldChange: (key: string | number, field: string, value: any) => void
  handleAddContactFromEmail: (key: string | number) => void
  startEditing: (key: string | number) => void
  cancelEditing: () => void
  saveRow: (key: string | number) => Promise<boolean>
  getTeamMembersForAPI: (recordId: string) => any[]
  hasPendingTeamMembers: () => boolean
}

export function useCompanyTeamController({
  mode = 'new',
  companyId = null,
  initialTeamMembers = [],
  onChange = null,
  enforcedFounderStatus = null,
  hideCategoryColumn = false,
  isExEmployees = false
}: UseCompanyTeamControllerProps): UseCompanyTeamControllerReturn {
  const teamMemberValidationRules = useMemo(() => {
    const baseRules = [
      createValidationRule(VALIDATION_TYPES.REQUIRED, 'Name', { fieldKey: 'contact' }),
      createValidationRule(VALIDATION_TYPES.REQUIRED, 'Email Address', { fieldKey: 'email' }),
      createValidationRule(VALIDATION_TYPES.EMAIL, 'Email Address', { fieldKey: 'email' })
    ]

    if (!(hideCategoryColumn && enforcedFounderStatus === true)) {
      baseRules.push(createValidationRule(VALIDATION_TYPES.REQUIRED, 'Job Category', { fieldKey: 'category' }))
    }

    return mergeValidationSets(baseRules, [
      createCustomValidationRule(
        'Email Address',
        (email, fieldName, record) => {
          if (hasEmailValidationError(record.key, email)) {
            return 'Email address already exists'
          }
          return null
        },
        'email'
      )
    ])
  }, [hideCategoryColumn, enforcedFounderStatus])

  const relationshipCategoriesRaw = useSelector((state: RootState) => state.globalConfigs.relationship_categories)

  const relationshipCategories = useMemo(() => relationshipCategoriesRaw || [], [relationshipCategoriesRaw])

  const founderCategoryOption = useMemo(() => {
    if (!relationshipCategories || relationshipCategories.length === 0) return null
    const founderCat = relationshipCategories.find((cat) => cat.category === 'Founder')
    if (!founderCat) return null
    return {
      value: founderCat.id,
      label: founderCat.category,
      id: founderCat.id,
      category: founderCat.category,
      relationship_type_id: founderCat.relationship_type_id
    }
  }, [relationshipCategories])

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(initialTeamMembers)
  const [editingRowKey, setEditingRowKey] = useState<string | number | null>(null)
  const [originalMemberData, setOriginalMemberData] = useState<TeamMember | null>(null)
  const [savingRowKey, setSavingRowKey] = useState<string | number | null>(null)
  const [deletingRowKey, setDeletingRowKey] = useState<string | number | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({})
  const [emailWarnings, setEmailWarnings] = useState<Record<string | number, EmailWarning>>({})
  const [firstErrorFieldKey, setFirstErrorFieldKey] = useState<FirstErrorFieldKey | null>(null)
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string | number, number>>({})
  const [savedEntries, setSavedEntries] = useState<Set<string | number>>(new Set())
  const initialTeamMembersRef = useRef<TeamMember[]>(initialTeamMembers)
  const pendingOnChangeRef = useRef<{ clearedMembers: TeamMember[]; isNewMode: boolean } | null>(null)
  const hasLocalChangesRef = useRef<boolean>(false)
  const emailValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const teamMembersRef = useRef<TeamMember[]>(teamMembers)
  const modeRef = useRef<'new' | 'existing'>(mode)
  const onChangeRef = useRef<UseCompanyTeamControllerProps['onChange']>(onChange)
  const originalMemberDataRef = useRef<TeamMember | null>(originalMemberData)
  const getTeamMembersForAPIRef = useRef<((recordId: string) => any[]) | null>(null)

  useEffect(() => {
    teamMembersRef.current = teamMembers
    modeRef.current = mode
    onChangeRef.current = onChange
    originalMemberDataRef.current = originalMemberData
  }, [teamMembers, mode, onChange, originalMemberData])

  const transformNewMemberForAPI = useCallback(
    (member: TeamMember, recordId: string): any[] | null => {
      const contact = member.contact
      if (!contact || !contact.value) {
        return null
      }

      const memberWithEnforcedStatus = { ...member }
      if (enforcedFounderStatus !== null) {
        memberWithEnforcedStatus.founder = enforcedFounderStatus
      }

      const relationships = []

      const contactId = contact.value === 'new' ? crypto.randomUUID() : contact.value
      const categoryId = categoryIdOf(memberWithEnforcedStatus.category)

      const { firstName, lastName } = parseContactNameFromLabel(contact)

      let contactAction = ''
      if (contact.value === 'new') {
        contactAction = ACTION_TYPE_INSERT
      } else {
        const originalEmail = contact.data?.email || null
        const currentEmail = memberWithEnforcedStatus.email || null
        const originalMobile = contact.data?.mobile_phone || null
        const currentMobile = memberWithEnforcedStatus.contactNumber || null

        const emailChanged = originalEmail !== currentEmail
        const mobileChanged = originalMobile !== currentMobile
        contactAction = determineContactAction(contact.value, emailChanged, mobileChanged)
      }

      relationships.push(
        buildRelationshipObject({
          relationshipId: null,
          relationshipType: RELATIONSHIP_TYPE_EMPLOYEE,
          categoryId,
          action: ACTION_TYPE_INSERT,
          companyId: recordId,
          contactId,
          member: memberWithEnforcedStatus,
          firstName,
          lastName,
          contactAction,
          isFounder: false,
          isExEmployees
        })
      )

      if (memberWithEnforcedStatus.founder) {
        const founderCategoryId = founderCategoryOption?.id || categoryId
        relationships.push(
          buildRelationshipObject({
            relationshipId: null,
            relationshipType: RELATIONSHIP_TYPE_FOUNDER,
            categoryId: founderCategoryId,
            action: ACTION_TYPE_INSERT,
            companyId: recordId,
            contactId,
            member: memberWithEnforcedStatus,
            firstName,
            lastName,
            contactAction: '',
            isFounder: true,
            isExEmployees
          })
        )
      }

      return transformAndValidateRelationships(relationships)
    },
    [founderCategoryOption, enforcedFounderStatus, isExEmployees]
  )

  const getTeamMembersForAPI = useCallback(
    (recordId: string): any[] => {
      if (modeRef.current !== 'new') {
        return []
      }

      try {
        const allRelationships = teamMembersRef.current
          .filter((member) => member.contact && member.contact.value)
          .map((member) => transformNewMemberForAPI(member, recordId))
          .filter(Boolean)
          .flat()

        allRelationships.forEach((entry) => {
          try {
            validateCompanyTeamEntry(entry)
          } catch (error) {
            console.error('Validation failed for team member entry:', error)
            const errorMessage = error instanceof Error ? error.message : 'Validation failed'
            showErrorToast(`Team member validation failed: ${errorMessage}`, TOAST_CONFIG)
            throw error
          }
        })

        return allRelationships
      } catch (error) {
        console.error('Error transforming team members for API:', error)
        throw error
      }
    },
    [transformNewMemberForAPI]
  )

  useEffect(() => {
    getTeamMembersForAPIRef.current = getTeamMembersForAPI
  }, [getTeamMembersForAPI])

  const notifyParentChange = useCallback(
    (members: TeamMember[], useRefs: boolean = true) => {
      const currentOnChange = useRefs ? onChangeRef.current : onChange
      const currentMode = useRefs ? modeRef.current : mode
      const currentGetAPI = getTeamMembersForAPIRef.current

      if (currentOnChange) {
        if (currentMode === 'new') {
          currentOnChange(members, currentGetAPI || undefined)
        } else {
          currentOnChange(members)
        }
      }
    },
    [onChange, mode]
  )

  const removeMemberAndNotify = useCallback(
    (key: string | number) => {
      const updatedMembers = teamMembers.filter((member) => member.key !== key)
      setTeamMembers(updatedMembers)
      hasLocalChangesRef.current = true
      notifyParentChange(updatedMembers)
    },
    [teamMembers, notifyParentChange]
  )

  const buildRelationshipPayloadForRemoval = (
    member: TeamMember,
    key: string | number,
    action: string,
    enforcedFounderStatus: boolean | null
  ): any[] => {
    const apiPayload = []
    const isValidRelationshipId = (id: string | number | null | undefined): id is string => {
      return typeof id === 'string' && !id.startsWith('team_')
    }

    if (enforcedFounderStatus === true) {
      const founderRelationshipId = member.founderRelationshipId
      if (isValidRelationshipId(founderRelationshipId)) {
        apiPayload.push({
          [CompanyTeamEntryFields.contact_accounts__id]: founderRelationshipId,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: action
        })
      }
    } else if (enforcedFounderStatus === false) {
      const employeeRelationshipId = member.employeeRelationshipId || key
      if (isValidRelationshipId(employeeRelationshipId)) {
        apiPayload.push({
          [CompanyTeamEntryFields.contact_accounts__id]: employeeRelationshipId,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: action
        })
      }
    } else {
      const employeeRelationshipId = member.employeeRelationshipId || key
      if (isValidRelationshipId(employeeRelationshipId)) {
        apiPayload.push({
          [CompanyTeamEntryFields.contact_accounts__id]: employeeRelationshipId,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: action
        })
      }

      const founderRelationshipId = member.founderRelationshipId
      if (isValidRelationshipId(founderRelationshipId) && member.founder) {
        apiPayload.push({
          [CompanyTeamEntryFields.contact_accounts__id]: founderRelationshipId,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: action
        })
      }
    }

    return apiPayload
  }

  const extractRelationshipIds = (
    apiData: any[]
  ): { employeeRelationshipId: string | null; founderRelationshipId: string | null; contactId: string | null } => {
    let employeeRelationshipId = null
    let founderRelationshipId = null
    let contactId = null

    for (const entry of apiData) {
      const relationshipId = entry[CompanyTeamEntryFields.contact_accounts__id]
      const isFounder = entry[CompanyTeamEntryFields.prog__founder] || false
      const entryContactId = entry[CompanyTeamEntryFields.contacts__id]

      if (relationshipId) {
        if (isFounder) {
          founderRelationshipId = relationshipId
        } else {
          employeeRelationshipId = relationshipId
        }
      }

      if (entryContactId) {
        contactId = entryContactId
      }
    }

    return { employeeRelationshipId, founderRelationshipId, contactId }
  }

  const extractContactData = (value: any): { email: string; contactNumber: string } => {
    let email = ''
    let contactNumber = ''

    if (value.email !== undefined && value.email) {
      email = value.email
    } else if (value.data) {
      const contactData = value.data
      email =
        contactData.email || contactData.contacts__email || contactData[CompanyTeamEntryFields.contacts__email] || ''
    }

    if (value.mobile_phone !== undefined && value.mobile_phone) {
      contactNumber = value.mobile_phone
    } else if (value.contactNumber !== undefined && value.contactNumber) {
      contactNumber = value.contactNumber
    } else if (value.data) {
      const contactData = value.data
      contactNumber =
        contactData.mobile_phone ||
        contactData.contactNumber ||
        contactData.contacts__mobile_phone ||
        contactData[CompanyTeamEntryFields.contacts__mobile_phone] ||
        ''
    }

    return { email, contactNumber }
  }

  const getDuplicateMessage = (
    duplicateByContact: boolean,
    duplicateByEmail: boolean,
    contactLabel: string,
    memberEmail: string
  ): string => {
    if (duplicateByContact) {
      return `${contactLabel} is already in the team (only one entry per contact allowed)`
    } else if (duplicateByEmail) {
      return `Email address "${memberEmail}" is already in use by another team member`
    }
    return `Duplicate entry for ${contactLabel}`
  }

  const createContactOptionFromData = (contactData: {
    id: string
    name: string
    email?: string
    mobile_phone?: string
  }) => {
    return {
      value: contactData.id,
      label: contactData.name,
      email: contactData.email || '',
      mobile_phone: contactData.mobile_phone || '',
      data: {
        id: contactData.id,
        first_name: contactData.name.split(' ')[0] || '',
        last_name: contactData.name.split(' ').slice(1).join(' ') || '',
        email: contactData.email || '',
        mobile_phone: contactData.mobile_phone || ''
      }
    }
  }

  const clearEditingState = () => {
    setEditingRowKey(null)
    setOriginalMemberData(null)
    setFirstErrorFieldKey(null)
  }

  const handleValidationErrors = (
    validationResult: { isValid: boolean; errors: Record<string, string> },
    key: string | number
  ): boolean => {
    if (!validationResult.isValid) {
      const errorKey = `${key}-errors`
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: validationResult.errors
      }))

      const fieldPriority = ['contact', 'category', 'email', 'jobTitle', 'contactNumber', 'founder']
      const firstErrorField = fieldPriority.find((field) => validationResult.errors[field])

      if (firstErrorField) {
        setFirstErrorFieldKey({ rowKey: key, fieldKey: firstErrorField })
      }

      return false
    }
    return true
  }

  const clearMemberFields = (key: string | number): TeamMember => {
    return {
      key: key,
      contact: null,
      email: '',
      contactNumber: '',
      category: null,
      jobTitle: '',
      founder: false
    }
  }

  const handleDuplicateMember = useCallback(
    (
      key: string | number,
      isNew: boolean,
      contactLabel: string,
      memberEmail: string,
      duplicateByContact: boolean = false,
      duplicateByEmail: boolean = false
    ) => {
      toast.warning(getDuplicateMessage(duplicateByContact, duplicateByEmail, contactLabel, memberEmail), TOAST_CONFIG)

      const errorKey = `${key}-errors`
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
      setFirstErrorFieldKey(null)

      if (isNew) {
        setFieldResetKeys((prev) => ({
          ...prev,
          [key]: (prev[key] || 0) + 1
        }))

        const currentMembers = teamMembersRef.current || []
        const clearedMembers = currentMembers.map((member) => (member.key === key ? clearMemberFields(key) : member))

        teamMembersRef.current = clearedMembers
        setTeamMembers(clearedMembers)
        hasLocalChangesRef.current = true
        setTimeout(() => {
          hasLocalChangesRef.current = false
        }, 100)

        notifyParentChange(clearedMembers, true)
      } else {
        const currentMembers = teamMembersRef.current || []
        const restoredMembers = currentMembers.map((member) =>
          member.key === key ? originalMemberDataRef.current || member : member
        )

        teamMembersRef.current = restoredMembers
        setTeamMembers(restoredMembers)
        hasLocalChangesRef.current = true
        setTimeout(() => {
          hasLocalChangesRef.current = false
        }, 100)

        notifyParentChange(restoredMembers, true)
      }
    },
    [notifyParentChange]
  )

  const scrollToLeftAndFocus = () => {
    const selectors = [
      '.ant-table-content',
      '.ant-table-container .ant-table-content',
      '.ant-table-body',
      '.ant-table-container',
      '.compact-team-table .ant-table-content',
      '.compact-team-table .ant-table-container',
      '.ant-table-tbody'
    ]

    let tableContainer = null
    for (const selector of selectors) {
      const element = document.querySelector(selector)
      if (element && element.scrollWidth > element.clientWidth) {
        tableContainer = element
        break
      }
    }

    if (tableContainer) {
      tableContainer.scrollTo({ left: 0, behavior: 'smooth' })
      tableContainer.scrollLeft = 0
    }

    setTimeout(() => {
      const addButton = document.querySelector<HTMLElement>('[data-testid="add-team-member-button"]')
      if (addButton) {
        addButton.focus()
      }
    }, 300)
  }

  useEffect(() => {
    return () => {
      if (emailValidationTimeoutRef.current) {
        clearTimeout(emailValidationTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (pendingOnChangeRef.current) {
      const { clearedMembers, isNewMode } = pendingOnChangeRef.current
      pendingOnChangeRef.current = null
      notifyParentChange(clearedMembers, false)
    }
  }, [notifyParentChange])

  useEffect(() => {
    if (hasLocalChangesRef.current) {
      setTimeout(() => {
        hasLocalChangesRef.current = false
      }, 100)
      return
    }

    if (editingRowKey) {
      const isNewEntryKey = isNewEntry(editingRowKey)
      const isLocallyAddedEntry = !initialTeamMembers.some((m) => m.key === editingRowKey)

      if (isNewEntryKey || isLocallyAddedEntry) {
        return
      }
    }

    const hasChanged =
      initialTeamMembers.length !== initialTeamMembersRef.current.length ||
      JSON.stringify(initialTeamMembers) !== JSON.stringify(initialTeamMembersRef.current)

    if (hasChanged) {
      const locallyAddedEntries = teamMembers.filter((member) => {
        const memberContactId = member.contact?.value
        if (!memberContactId) return true

        const existsInInitial = initialTeamMembers.some((initial) => {
          const initialContactId = initial.contact?.value
          return initialContactId === memberContactId
        })

        return !existsInInitial && !initialTeamMembers.some((initial) => initial.key === member.key)
      })

      const mergedMembers = [...locallyAddedEntries, ...initialTeamMembers]

      if (editingRowKey && isNewEntry(editingRowKey)) {
        const editingMember = teamMembers.find((m) => m.key === editingRowKey)
        if (editingMember && !mergedMembers.some((m) => m.key === editingRowKey)) {
          mergedMembers.unshift(editingMember)
        }
      }

      setTeamMembers(mergedMembers)
      initialTeamMembersRef.current = initialTeamMembers
    }
  }, [initialTeamMembers, editingRowKey, teamMembers])

  const availableCategoryOptions = useMemo(() => {
    if (!relationshipCategories || relationshipCategories.length === 0) {
      return []
    }
    const filteredCategories = relationshipCategories.filter(
      (cat) => cat.relationship_type_id === RELATIONSHIP_TYPE_EMPLOYEE
    )
    return filteredCategories.map((cat) => ({
      value: cat.id,
      label: cat.category,
      id: cat.id,
      category: cat.category,
      relationship_type_id: cat.relationship_type_id
    }))
  }, [relationshipCategories])

  useEffect(() => {
    if (availableCategoryOptions.length > 0 && teamMembers.length > 0) {
      const hasUuidCategories = teamMembers.some((member) => member.category && typeof member.category === 'string')

      if (hasUuidCategories) {
        const transformedMembers = teamMembers.map((member) => {
          if (member.category && typeof member.category === 'string') {
            const categoryOption = availableCategoryOptions.find((option) => option.value === member.category)

            if (categoryOption) {
              return {
                ...member,
                category: {
                  value: categoryOption.value,
                  label: categoryOption.label,
                  id: categoryOption.id,
                  category: categoryOption.category,
                  relationship_type_id: categoryOption.relationship_type_id
                }
              }
            }
          }
          return member
        })

        const hasChanges = transformedMembers.some((member, index) => {
          const original = teamMembers[index]
          return original && original.category !== member.category
        })

        if (hasChanges) {
          setTeamMembers(transformedMembers)
        }
      }
    }
  }, [availableCategoryOptions, teamMembers])

  const handleAddMember = () => {
    const newKey = generateNewKey()
    const defaultFounderStatus = enforcedFounderStatus !== null ? enforcedFounderStatus : false
    const defaultCategory =
      enforcedFounderStatus === true && hideCategoryColumn && founderCategoryOption ? founderCategoryOption : null
    const newMember: TeamMember = {
      key: newKey,
      contact: null,
      founder: defaultFounderStatus,
      category: defaultCategory,
      jobTitle: '',
      email: '',
      contactNumber: ''
    }
    const updatedMembers = [newMember, ...teamMembers]

    hasLocalChangesRef.current = true

    setEditingRowKey(newKey)
    setFirstErrorFieldKey({ rowKey: newKey, fieldKey: 'contact' })

    setTeamMembers(updatedMembers)
    initialTeamMembersRef.current = updatedMembers
  }

  const handleRemoveMember = async (key: string | number): Promise<void> => {
    try {
      setDeletingRowKey(key)

      if (editingRowKey === key) {
        setEditingRowKey(null)
      }

      setSavedEntries((prev) => {
        const updated = new Set(prev)
        updated.delete(key)
        return updated
      })

      if (mode === 'existing' && companyId && !isNewEntry(key)) {
        const memberToDelete = teamMembers.find((member) => member.key === key)
        if (!memberToDelete) {
          throw new Error('Member not found')
        }

        const apiPayload = buildRelationshipPayloadForRemoval(
          memberToDelete,
          key,
          ACTION_TYPE_DELETE,
          enforcedFounderStatus
        )

        if (apiPayload.length === 0) {
          throw new Error('No valid relationship IDs found for deletion')
        }

        const result = await processCompanyTeamRelationships(companyId, apiPayload)

        if (!result.success) {
          handleAPIError(result, 'Failed to delete team member')
        }

        removeMemberAndNotify(key)
        toast.success('Team member deleted successfully', TOAST_SUCCESS_CONFIG)
      } else {
        removeMemberAndNotify(key)
      }
    } catch (error) {
      console.error('Failed to remove team member:', error)
    } finally {
      setDeletingRowKey(null)
    }
  }

  const handleResignMember = async (key: string | number): Promise<void> => {
    try {
      setDeletingRowKey(key)

      if (editingRowKey === key) {
        setEditingRowKey(null)
      }

      if (mode === 'existing' && companyId && !isNewEntry(key)) {
        const memberToResign = teamMembers.find((member) => member.key === key)
        if (!memberToResign) {
          throw new Error('Member not found')
        }

        const apiPayload = buildRelationshipPayloadForRemoval(memberToResign, key, ACTION_TYPE_RESIGN, null)

        if (apiPayload.length === 0) {
          throw new Error('No valid relationship IDs found for resignation')
        }

        const result = await processCompanyTeamRelationships(companyId, apiPayload)

        if (!result.success) {
          handleAPIError(result, 'Failed to resign team member')
        }

        removeMemberAndNotify(key)
        toast.success('Team member resigned successfully', TOAST_SUCCESS_CONFIG)
      } else {
        removeMemberAndNotify(key)
      }

      scrollToLeftAndFocus()
    } catch (error) {
      console.error('Failed to resign team member:', error)
    } finally {
      setDeletingRowKey(null)
    }
  }

  const handleAddContactFromEmail = useCallback(
    (key: string | number): void => {
      const warning = emailWarnings[key]
      if (!warning || !warning.contact) {
        return
      }

      const contactOption = createContactOptionFromData(warning.contact)

      setTeamMembers((prevMembers) => {
        const updated = prevMembers.map((member) => {
          if (member.key === key) {
            return {
              ...member,
              contact: contactOption,
              contactNumber: warning.contact.mobile_phone || member.contactNumber || ''
            }
          }
          return member
        })

        if (mode === 'existing') {
          hasLocalChangesRef.current = true
        }

        setEmailWarnings((prev) => {
          const updated = { ...prev }
          delete updated[key]
          return updated
        })

        notifyParentChange(updated)

        return updated
      })
    },
    [emailWarnings, mode, notifyParentChange]
  )

  const handleFieldChange = useCallback((key: string | number, field: string, value: any): void => {
    if (field === 'founder' && enforcedFounderStatus !== null && value !== enforcedFounderStatus) {
      return
    }

    if (field === 'contact' && value) {
      const tempMember: TeamMember = {
        key: key,
        contact: value,
        email: '',
        contactNumber: '',
        category: null,
        jobTitle: '',
        founder: false
      }

      const otherMembersForContactCheck = teamMembersRef.current.filter((member) => member.key !== key)
      const isContactDuplicate = isDuplicateTeamMember(tempMember, otherMembersForContactCheck, null)

      if (isContactDuplicate) {
        const contactLabel = value?.label || value?.name || 'Unknown contact'
        toast.warning(`${contactLabel} is already in the team (only one entry per contact allowed)`, TOAST_CONFIG)

        const errorKey = `${key}-errors`
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[errorKey]
          return newErrors
        })
        setFirstErrorFieldKey(null)

        if (isNewEntry(key)) {
          setFieldResetKeys((prev) => ({
            ...prev,
            [key]: (prev[key] || 0) + 1
          }))
        }

        handleDuplicateMember(key, isNewEntry(key), contactLabel, '', true, false)
        return
      }
    }

    const currentMember = teamMembersRef.current.find((member) => member.key === key)

    const updatedMembers = teamMembersRef.current.map((member) => {
      if (member.key === key) {
        let updatedMember
        if (isNewEntry(key) && !currentMember?.contact && !currentMember?.email) {
          updatedMember = {
            key: member.key,
            contact: null,
            email: '',
            contactNumber: '',
            category: null,
            jobTitle: '',
            founder: false,
            [field]: value
          }
        } else {
          updatedMember = { ...member, [field]: value }
        }

        if (field === 'contact') {
          setEmailWarnings((prev) => {
            const updated = { ...prev }
            delete updated[key]
            return updated
          })

          if (value) {
            const { email, contactNumber } = extractContactData(value)
            updatedMember = {
              ...updatedMember,
              email,
              contactNumber
            }
          } else {
            updatedMember = {
              ...updatedMember,
              email: '',
              contactNumber: ''
            }
          }
        }

        if (field === 'email') {
          setEmailWarnings((prev) => {
            const updated = { ...prev }
            delete updated[key]
            return updated
          })
        }

        const isNewContact = updatedMember.contact?.value === 'new' || updatedMember.contact?.value?.endsWith('_new')
        if (field === 'email' && value && (!updatedMember.contact || isNewContact)) {
          if (emailValidationTimeoutRef.current) {
            clearTimeout(emailValidationTimeoutRef.current)
          }

          emailValidationTimeoutRef.current = setTimeout(async () => {
            const emailValue = value.trim()

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(emailValue)) {
              return
            }

            try {
              const BASE_URL = getBackendBaseUrl()
              const headers = await getAuthHeaders()

              const response = await fetch(
                buildFullUrl(BASE_URL, API_ROUTES.CONTACTS.VALIDATE_EMAIL, {}, { email: emailValue }),
                {
                  method: API_ROUTES.CONTACTS.VALIDATE_EMAIL.method,
                  headers
                }
              )

              if (response.ok) {
                const result = await response.json()

                if (result.exists && result.contact) {
                  const contactData = result.contact

                  setEmailWarnings((prev) => ({
                    ...prev,
                    [key]: {
                      contact: contactData,
                      email: emailValue
                    }
                  }))
                } else {
                  setEmailWarnings((prev) => {
                    const updated = { ...prev }
                    delete updated[key]
                    return updated
                  })
                }
              }
            } catch (error) {
              console.error('Error validating email:', error)
            }
          }, 500)
        }

        return updatedMember
      }
      return member
    })

    const updatedMember = updatedMembers.find((member) => member.key === key)

    if (field === 'contact' && !value) {
      setTeamMembers(updatedMembers)
      notifyParentChange(updatedMembers, true)
      return
    }

    if (!updatedMember || (!updatedMember.contact && !updatedMember.email)) {
      setTeamMembers(updatedMembers)
      notifyParentChange(updatedMembers, true)
      return
    }

    const otherMembers = teamMembersRef.current.filter((member) => member.key !== key)
    const isDuplicate = isDuplicateTeamMember(updatedMember, otherMembers, null)

    if (updatedMember && isDuplicate) {
      const contactLabel = updatedMember.contact?.label || updatedMember.contact?.name || 'Unknown contact'
      const memberEmail = updatedMember.email || ''

      const duplicateByContact = otherMembers.some(
        (member) =>
          getComparableContact(member.contact) === getComparableContact(updatedMember.contact) &&
          getComparableContact(updatedMember.contact)
      )

      const duplicateByEmail = otherMembers.some(
        (member) => (member.email || '').toLowerCase().trim() === memberEmail.toLowerCase().trim() && memberEmail.trim()
      )

      toast.warning(getDuplicateMessage(duplicateByContact, duplicateByEmail, contactLabel, memberEmail), TOAST_CONFIG)

      const errorKey = `${key}-errors`
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
      setFirstErrorFieldKey(null)

      if (isNewEntry(key)) {
        setFieldResetKeys((prev) => ({
          ...prev,
          [key]: (prev[key] || 0) + 1
        }))
      }

      handleDuplicateMember(key, isNewEntry(key), contactLabel, memberEmail, duplicateByContact, duplicateByEmail)
      return
    }

    setTeamMembers(updatedMembers)
    if (modeRef.current === 'existing') {
      hasLocalChangesRef.current = true
    }

    notifyParentChange(updatedMembers, true)
  }, [])

  const startEditing = useCallback(
    (key: string | number): void => {
      if (editingRowKey && editingRowKey !== key) {
        return
      }

      const originalMember = teamMembersRef.current.find((member) => member.key === key)
      if (originalMember) {
        setOriginalMemberData({ ...originalMember })
      }

      if (isNewEntry(key)) {
        hasLocalChangesRef.current = true
      }

      setEditingRowKey(key)
      setFirstErrorFieldKey({ rowKey: key, fieldKey: 'contact' })
    },
    [editingRowKey]
  )

  const cancelEditing = () => {
    if (editingRowKey) {
      if (originalMemberData) {
        const updatedMembers = teamMembers.map((member) => (member.key === editingRowKey ? originalMemberData : member))
        setTeamMembers(updatedMembers)
        hasLocalChangesRef.current = true
        notifyParentChange(updatedMembers)
      } else {
        const updatedMembers = teamMembers.filter((member) => member.key !== editingRowKey)
        setTeamMembers(updatedMembers)
        hasLocalChangesRef.current = true
        notifyParentChange(updatedMembers)
      }
    }

    clearEditingState()
  }

  const saveRow = async (key: string | number): Promise<boolean> => {
    const memberToSave = teamMembers.find((member) => member.key === key)

    if (!memberToSave) {
      return false
    }

    if (hideCategoryColumn && enforcedFounderStatus === true && founderCategoryOption && !memberToSave.category) {
      memberToSave.category = founderCategoryOption
      setTeamMembers((prev) =>
        prev.map((member) => (member.key === key ? { ...member, category: founderCategoryOption } : member))
      )
    }

    const errorKey = `${key}-errors`
    setValidationErrors((prev) => ({
      ...prev,
      [errorKey]: {}
    }))
    setFirstErrorFieldKey(null)

    const validationResult = validateAndShowErrors(memberToSave, teamMemberValidationRules)

    if (!handleValidationErrors(validationResult, key)) {
      return false
    }

    if (isDuplicateTeamMember(memberToSave, teamMembers, key)) {
      toast.warning('Cannot save - this contact or email address is already in the team', TOAST_CONFIG)
      return false
    }

    let memberToSaveWithEnforcedStatus = { ...memberToSave }
    if (enforcedFounderStatus !== null && isNewEntry(key) && memberToSave.founder !== enforcedFounderStatus) {
      memberToSaveWithEnforcedStatus.founder = enforcedFounderStatus
      const updatedMembers = teamMembers.map((member) => (member.key === key ? memberToSaveWithEnforcedStatus : member))
      setTeamMembers(updatedMembers)
    }

    if (mode === 'new') {
      setSavedEntries((prev) => new Set([...prev, key]))

      notifyParentChange(teamMembers)
      clearEditingState()
      scrollToLeftAndFocus()
      return true
    }

    if (mode === 'existing' && companyId) {
      try {
        setSavingRowKey(key)

        const memberToSaveForAPI = memberToSaveWithEnforcedStatus
        if (!memberToSaveForAPI) {
          throw new Error('Member not found')
        }

        if (isNewEntry(key)) {
          const apiData = transformNewMemberForAPI(memberToSaveForAPI, companyId)
          if (!apiData) {
            // Only a member without a contact transforms to null, and the Name validation above rejected that.
            throw new Error('Team member has no contact')
          }
          const { employeeRelationshipId, founderRelationshipId, contactId } = extractRelationshipIds(apiData)

          const result = await processCompanyTeamRelationships(companyId, apiData)

          if (!result.success) {
            handleAPIError(result, 'Failed to save team member')
          }

          const updatedMembers = teamMembers.map((member) => {
            if (member.key === key) {
              const updatedMember = {
                ...memberToSaveForAPI,
                key: employeeRelationshipId || key,
                employeeRelationshipId: employeeRelationshipId,
                founderRelationshipId: founderRelationshipId
              }

              if (contactId && memberToSaveForAPI.contact?.value === 'new') {
                updatedMember.contact = {
                  ...memberToSaveForAPI.contact,
                  value: contactId,
                  id: contactId
                }
              }

              return updatedMember
            }
            return member
          })

          setTeamMembers(updatedMembers)
          hasLocalChangesRef.current = true
          clearEditingState()
          notifyParentChange(updatedMembers)
          toast.success('Team member saved successfully', TOAST_SUCCESS_CONFIG)
          scrollToLeftAndFocus()
          return true
        } else {
          const originalMember = originalMemberData
          if (!originalMember) {
            throw new Error('Original member data not found')
          }

          const memberToSaveWithPreservedIds = {
            ...memberToSaveForAPI,
            employeeRelationshipId:
              memberToSaveForAPI.employeeRelationshipId || originalMember?.employeeRelationshipId || null,
            founderRelationshipId:
              memberToSaveForAPI.founderRelationshipId || originalMember?.founderRelationshipId || null
          }

          const apiData = transformExistingMemberForAPI(
            memberToSaveWithPreservedIds,
            originalMember,
            companyId,
            founderCategoryOption,
            isExEmployees
          )

          if (apiData.length === 0) {
            clearEditingState()
            toast.info('No changes to save', TOAST_INFO_CONFIG)
            scrollToLeftAndFocus()
            return true
          }

          const result = await processCompanyTeamRelationships(companyId, apiData)

          if (!result.success) {
            handleAPIError(result, 'Failed to save team member')
          }

          clearEditingState()
          hasLocalChangesRef.current = true
          notifyParentChange(teamMembers)
          toast.success('Team member saved successfully', TOAST_SUCCESS_CONFIG)
          scrollToLeftAndFocus()
          return true
        }
      } catch (error) {
        console.error('Failed to save team member:', error)
        return false
      } finally {
        setSavingRowKey(null)
      }
    }

    clearEditingState()
    scrollToLeftAndFocus()
    return true
  }

  const isAddingNewMember = isNewEntry(editingRowKey)

  const hasPendingTeamMembers = (): boolean => {
    return isNewEntry(editingRowKey)
  }

  return {
    teamMembers,
    editingRowKey,
    availableCategoryOptions,
    founderCategoryOption,
    isAddingNewMember,
    savingRowKey,
    deletingRowKey,
    validationErrors,
    emailWarnings,
    firstErrorFieldKey,
    fieldResetKeys,
    savedEntries,
    handleAddMember,
    handleRemoveMember,
    handleResignMember,
    handleFieldChange,
    handleAddContactFromEmail,
    startEditing,
    cancelEditing,
    saveRow,
    getTeamMembersForAPI,
    hasPendingTeamMembers
  }
}
